import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export type UrlParamCodec<T> = {
  parse: (values: string[]) => T;
  serialize: (value: T) => string[];
};

type CodecMap = Record<string, UrlParamCodec<any>>;

type StateOf<M extends CodecMap> = {
  [K in keyof M]: M[K] extends UrlParamCodec<infer T> ? T : never;
};

export function stringParam(defaultValue = ''): UrlParamCodec<string> {
  return {
    parse: (values) => (values[0] ?? defaultValue).trim() || defaultValue,
    serialize: (value) => {
      const trimmed = value.trim();
      return trimmed && trimmed !== defaultValue ? [trimmed] : [];
    },
  };
}

export function nullableStringParam(): UrlParamCodec<string | null> {
  return {
    parse: (values) => values[0] ?? null,
    serialize: (value) => (value ? [value] : []),
  };
}

export function pageParam(): UrlParamCodec<number> {
  return {
    parse: (values) => {
      const parsed = Number.parseInt(values[0] ?? '', 10);
      return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    },
    serialize: (value) => (value > 1 ? [String(value)] : []),
  };
}

export function numberParam(defaultValue: number): UrlParamCodec<number> {
  return {
    parse: (values) => {
      const parsed = Number.parseInt(values[0] ?? '', 10);
      return Number.isFinite(parsed) ? parsed : defaultValue;
    },
    serialize: (value) => (value === defaultValue ? [] : [String(value)]),
  };
}

export function chainFilterParam(): UrlParamCodec<number | 'all'> {
  return {
    parse: (values) => {
      const parsed = Number.parseInt(values[0] ?? '', 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 'all';
    },
    serialize: (value) => (value === 'all' ? [] : [String(value)]),
  };
}

export function enumParam<T extends string, D extends string>(
  allowedValues: readonly T[],
  defaultValue: D
): UrlParamCodec<T | D> {
  return {
    parse: (values) => {
      const raw = values[0];
      return raw && (allowedValues as readonly string[]).includes(raw) ? (raw as T) : defaultValue;
    },
    serialize: (value) => (value === defaultValue ? [] : [value]),
  };
}

export function stringListParam(): UrlParamCodec<string[]> {
  return {
    parse: (values) => values,
    serialize: (values) => values,
  };
}

/**
 * Keeps a set of dashboard filter values (filters, query, sort, page, selection)
 * in sync with URL search params so refresh/back navigation and shared links
 * restore the view. Params at their default value are omitted from the URL.
 *
 * The codec map must be static for the lifetime of the component; it is
 * captured on first render. Updates apply all changed keys in a single
 * `replace: true` navigation, and unchanged keys keep referential identity so
 * they stay safe to use in dependency arrays.
 */
export function useUrlFilterState<M extends CodecMap>(
  codecs: M
): [StateOf<M>, (partial: Partial<StateOf<M>>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const codecsRef = useRef(codecs);
  const cacheRef = useRef<{ rawByKey: Map<string, string>; parsedByKey: Map<string, unknown> }>({
    rawByKey: new Map(),
    parsedByKey: new Map(),
  });

  const state = useMemo(() => {
    const next = {} as StateOf<M>;
    const cache = cacheRef.current;
    for (const key of Object.keys(codecsRef.current)) {
      const values = searchParams.getAll(key);
      const raw = values.join('\u0000');
      if (!cache.parsedByKey.has(key) || cache.rawByKey.get(key) !== raw) {
        cache.rawByKey.set(key, raw);
        cache.parsedByKey.set(key, codecsRef.current[key].parse(values));
      }
      next[key as keyof M] = cache.parsedByKey.get(key) as StateOf<M>[keyof M];
    }
    return next;
  }, [searchParams]);

  const update = useCallback(
    (partial: Partial<StateOf<M>>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(partial)) {
            const codec = codecsRef.current[key];
            if (!codec || value === undefined) continue;
            next.delete(key);
            for (const raw of codec.serialize(value)) {
              next.append(key, raw);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [state, update];
}
